import React from "react";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { ProductCheckboxList } from "components/ProductCheckboxList";
import { routes } from "utilities/routing/generatedRoutes";
import { getInvoiceV1NewSections } from "invoice/getInvoiceV1NewSections";
import { useInvoiceV1NewForm } from "invoice/hooks";
import { InvoiceNumber } from "components/Invoice/InvoiceNumber";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { getEnabledV1DocumentTypes, getNewLabelForDocumentType } from "utilities/documentUtils";
interface Props {
  viewModel: server.viewmodel.InvoiceV1CopyFrontModel;
}

export const InvoiceCopyEditSection = (props: Props) => {
  const { viewModel } = props;
  const { invoice } = viewModel;
  const [documentTypeToCopyTo, setDocumentTypeToCopyTo] = React.useState<server.dto.DocumentTypes>("invoice");
  const [selectedProducts, setSelectedProducts] = React.useState<number[]>(viewModel.products.map(p => p.productId));

  const hook = useInvoiceV1NewForm({
    initialAddressee: invoice.company,
    initialContact: invoice.contact,
    initialOwner: invoice.owner,
    initialConcerning: invoice.concerning || "",
    initialCostCenter: invoice.costCenter,
    allArtistRiders: viewModel.artistRiders
  });

  const onDocumentTypeChange = (type: server.dto.DocumentTypes) => {
    setDocumentTypeToCopyTo(type);
    hook.setIsDraft(type === "invoice" ? site.currentEnvironment.invoice.legacyDefaultDraft : false);
  }

  const submit = async () => {
    if (documentTypeToCopyTo === "invoice") {
      return window.CoreApi.Invoice.Copy({
        sourceDocumentId: invoice.invoiceId,
        productIds: selectedProducts,
        invoice: {
          isDraft: hook.isDraft,
          bookkeepingId: hook.bookkeeping!.bookkeepingId,
          ownerUserId: hook.owner.userId,
          invoiceDate: hook.invoiceDate.toDate(),
          dueInDays: hook.dueInDays,
          currency: hook.currency,
          exchangeRate: hook.exchangeRate,
          concerning: hook.concerning,
          addresseeCompanyId: hook.addressee?.companyId,
          contactId: hook.contact?.contactId,
          bankAccountId: hook.bankAccount?.bankAccountId,
          costCenterId: hook.costCenter?.costCenterId,
        } satisfies Required<server.dto.CreateInvoiceV1Instruction>
      } satisfies Required<server.dto.CopyInvoiceInstruction>);
    } else {
      const instruction = {
        sourceDocumentId: invoice.invoiceId,
        productIds: selectedProducts,
        document: {
          documentType: documentTypeToCopyTo,
          artistRiderId: hook.artistRider?.artistRiderId,
          bookkeepingId: hook.bookkeeping.bookkeepingId,
          addresseeCompanyId: hook.addressee.companyId,
          contactId: hook.contact?.contactId,
          currency: hook.currency,
          dueInDays: hook.dueInDays,
          exchangeRate: hook.exchangeRate,
          isDraft: hook.isDraft,
          ownerUserId: hook.owner.userId,
          sendDate: hook.invoiceDate.toDate(),
          concerning: hook.concerning,
        } satisfies Required<server.dto.CreateDocumentV1Instruction>,
      } satisfies Required<server.dto.CopyDocumentV1Instruction>;
      let copyApi: typeof window.CoreApi.Contract.Copy;
      if (documentTypeToCopyTo === "contract") copyApi = window.CoreApi.Contract.Copy;
      else if (documentTypeToCopyTo === "quote") copyApi = window.CoreApi.Quote.Copy;
      else if (documentTypeToCopyTo === "purchaseAgreement") copyApi = window.CoreApi.PurchaseAgreement.Copy;
      return copyApi(instruction);
    }
  };

  const documentTypes = getEnabledV1DocumentTypes();

  return <EditSectionTemplate
    submit={submit}
    redirectUrl={(itemId) => {
      if (documentTypeToCopyTo === "invoice") return routes.invoiceV1_Detail(itemId).absolute;
      if (documentTypeToCopyTo === "contract") return routes.contract_Detail(itemId).absolute;
      if (documentTypeToCopyTo === "quote") return routes.quote_Detail(itemId).absolute;
      if (documentTypeToCopyTo === "purchaseAgreement") return routes.purchaseAgreement_Detail(itemId).absolute;
      else throw new Error("Unsupported document type for redirection: " + documentTypeToCopyTo);
    }}
    sections={[
      {
        key: "documentNumberAndType",
        type: "regular",
        title: RESX.GeneralLabels.Number,
        fields: [{
          key: "documentNumber",
          label: RESX.GeneralLabels.Number,
          renderEditNew: () => (
            <div className="main-section-template-fields-padding" style={{ paddingLeft: 0 }}>
              <InvoiceNumber invoiceNumber={invoice.documentNumber} locked={invoice.locked} />
            </div>
          )
        }, {
          key: "documentType",
          label: RESX.GeneralLabels.Type,
          renderEditNew: () => (
            <RadioGroup
              orientation="vertical"
              options={documentTypes.map(o => ({ label: o.text, value: o.type }))}
              value={documentTypeToCopyTo}
              onChange={onDocumentTypeChange}
            />
          )
        }]
      },
      getInvoiceV1NewSections(hook, {
        title: getNewLabelForDocumentType(documentTypeToCopyTo),
        displayArtistRider: documentTypeToCopyTo === "contract",
        displayBankAccount: documentTypeToCopyTo === "invoice",
        displayCostCenter: documentTypeToCopyTo === "invoice",
        draftLabel: documentTypeToCopyTo === "invoice" ? RESX.Document.ProForma : RESX.DocumentLifeCycle.Draft,
      }),
      {
        key: "products",
        type: "regular",
        hidden: viewModel.products.length === 0,
        title: RESX.Product.Products,
        fields: [{
          key: "products",
          label: RESX.Product.Products,
          renderEditNew: () => (
            <ProductCheckboxList
              products={viewModel.products}
              selectedProducts={selectedProducts}
              setSelectedProducts={setSelectedProducts}
            />
          )
        }]
      }]} />;
};

export default InvoiceCopyEditSection;

